// src/components/ProgramForm.js
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, Typography, Grid, Card, CardContent, useTheme
} from '@mui/material';
import { Add } from '@mui/icons-material';

const ProgramForm = ({ initialData, days, onSave, onCancel }) => {
    const theme = useTheme(); // ✅
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        durationDays: '',
        days: []
    });

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                description: initialData.description || '',
                price: initialData.price || '',
                durationDays: initialData.durationDays || '',
                days: initialData.days || []
            });
        }
    }, [initialData]);

    const handleChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleSubmit = () => {
        if (!formData.name.trim()) {
            alert('Внесете име на програмата');
            return;
        }
        if (!formData.price || formData.price <= 0) {
            alert('Внесете валидна цена');
            return;
        }

        const dataToSend = {
            name: formData.name,
            description: formData.description,
            price: Number(formData.price),
            durationDays: formData.durationDays ? Number(formData.durationDays) : null,
            days: formData.days.map(day => ({ id: day.id }))
        };

        if (initialData?.id) dataToSend.id = initialData.id;
        onSave(dataToSend);
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover fieldset': { borderColor: '#ff7eb9' },
            '&.Mui-focused fieldset': { borderColor: '#ff7eb9' }
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#ff7eb9' }
    };

    return (
        <Dialog
            open={true}
            onClose={onCancel}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    backgroundColor: theme.palette.background.paper, // ✅
                    border: '2px solid rgba(255,126,185,0.3)',
                    boxShadow: '0 8px 32px rgba(255,126,185,0.2)'
                }
            }}
        >
            {/* ===== TITLE ===== */}
            <DialogTitle sx={{
                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                color: 'white', textAlign: 'center',
                fontWeight: 'bold', fontSize: '1.5rem', py: 3
            }}>
                {initialData ? '✏️ Промени програма' : '🏋️‍♂️ Додај нова програма'}
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 4 }}>
                    {/* ===== OSNOVNI INFORMACII ===== */}
                    <Card sx={{
                        borderRadius: '16px',
                        backgroundColor: theme.palette.background.paper, // ✅
                        border: '1px solid rgba(255,126,185,0.2)',
                        boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
                        overflow: 'hidden'
                    }}>
                        {/* Accent bar */}
                        <Box sx={{
                            height: 5,
                            background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)'
                        }} />
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h5" sx={{
                                color: '#ff7eb9', mb: 3, fontWeight: 'bold',
                                display: 'flex', alignItems: 'center', gap: 1
                            }}>
                                📝 Основни информации
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Ime */}
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Име на програма *"
                                               value={formData.name}
                                               onChange={handleChange('name')}
                                               required sx={fieldSx} />
                                </Grid>

                                {/* Opis */}
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline rows={4}
                                               label="Опис на програмата"
                                               value={formData.description}
                                               onChange={handleChange('description')}
                                               placeholder="Опишете ја програмата, целите, за кој е наменета..."
                                               sx={fieldSx} />
                                    <Typography variant="body2" sx={{
                                        color: theme.palette.text.disabled,
                                        mt: 0.5, fontStyle: 'italic'
                                    }}>
                                        Опишете ги деталите за програмата, целите и корисниците
                                    </Typography>
                                </Grid>

                                {/* Cena */}
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth type="number"
                                               label="Цена (ден.) *"
                                               value={formData.price}
                                               onChange={handleChange('price')}
                                               required
                                               InputProps={{ inputProps: { min: 0 } }}
                                               placeholder="0"
                                               sx={fieldSx} />
                                </Grid>

                                {/* Vremetraenje */}
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth type="number"
                                               label="⏱️ Времетраење (денови)"
                                               value={formData.durationDays}
                                               onChange={handleChange('durationDays')}
                                               InputProps={{ inputProps: { min: 0 } }}
                                               placeholder="На пример: 30"
                                               helperText="Колку денови трае програмата?"
                                               sx={fieldSx} />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    {/* ✅ IZBOR NA DENOVI - TRGNAT */}
                    {/* ✅ REZIME NA PROGRAMATA - TRGNAT */}
                </Box>
            </DialogContent>

            {/* ===== ACTIONS ===== */}
            <DialogActions sx={{
                p: 3, gap: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper // ✅
            }}>
                <Button onClick={onCancel} variant="outlined"
                        sx={{
                            borderRadius: '12px', px: 4, py: 1.5,
                            color: '#ff6b6b', borderColor: '#ff6b6b',
                            fontWeight: 'bold',
                            '&:hover': {
                                borderColor: '#ff5252',
                                backgroundColor: 'rgba(255,107,107,0.1)'
                            }
                        }}>
                    ❌ Откажи
                </Button>
                <Button onClick={handleSubmit} variant="contained"
                        startIcon={<Add />}
                        sx={{
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            color: '#fff', fontWeight: 'bold',
                            px: 4, py: 1.5, borderRadius: '12px',
                            boxShadow: '0 4px 15px rgba(255,126,185,0.3)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #ff4a97, #9C27B0)',
                                boxShadow: '0 6px 20px rgba(255,126,185,0.4)',
                                transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                        }}>
                    {initialData ? '💾 Зачувај промени' : '🚀 Креирај програма'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProgramForm;